import { useEffect, useState } from 'react';
import { Alert, ControlLabel, FormControl, FormGroup, HelpBlock, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IOpoAddress, IOpoAddressOption, IOpoAddressRequest, OpoAddressType } from '@nkr/models/opo-address';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { IUsPsAddressResponse } from '@nkr/models/usps-address-response';
import { IAddressValidationResponse } from '@nkr/store/index';
import AddressValidation, { IAddress } from '@nkr/components/address-validation';
import { OpoModalButtons, OpoModalForm } from '../../opo-styles';

const maxText = (label: string, max: number) => z.string().trim().max(max, `${label} cannot be longer than ${max} characters`);

const addressSchema = z.object({
    typeId: z.string().min(1, 'Address type is required'),
    label: maxText('Name', 32),
    addressLine1: maxText('Street address', 64).min(1, 'Street address is required'),
    addressLine2: maxText('Suite / PO Box', 64),
    addressLine3: maxText('Attention', 64),
    city: maxText('City', 32).min(1, 'City is required'),
    stateId: z.string().min(1, 'State is required'),
    postalCode: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'Enter a 5-digit or ZIP+4 postal code'),
    note: maxText('Note', 200),
    firstName: maxText('First name', 50),
    lastName: maxText('Last name', 50),
    shipToPhoneNumber: maxText('Phone number', 50)
}).superRefine((x, ctx) => {
    if (Number(x.typeId) != OpoAddressType.StrongBox) {
        return;
    }

    const required: [keyof typeof x, string][] = [
        ['label', 'Company name is required'],
        ['firstName', 'First name is required'],
        ['lastName', 'Last name is required'],
        ['shipToPhoneNumber', 'Ship-to phone number is required']
    ];

    required.forEach(([field, message]) => {
        if (x[field] == '') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
        }
    });
});

type AddressFormValues = z.infer<typeof addressSchema>;

const emptyValues = (typeId: string): AddressFormValues => ({
    typeId,
    label: '',
    addressLine1: __ENVIRONMENT__ == 'dev' ? '100 Main Ave' : '',
    addressLine2: '',
    addressLine3: '',
    city: __ENVIRONMENT__ == 'dev' ? 'Norwalk' : '',
    stateId: __ENVIRONMENT__ == 'dev' ? '7' : '',
    postalCode: __ENVIRONMENT__ == 'dev' ? '06851' : '',
    note: '',
    firstName: '',
    lastName: '',
    shipToPhoneNumber: ''
});

const toFormValues = (address: IOpoAddress): AddressFormValues => ({
    typeId: String(address.typeId),
    label: address.label,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    addressLine3: address.addressLine3,
    city: address.city,
    stateId: address.stateId ? String(address.stateId) : '',
    postalCode: address.postalCode,
    note: address.note,
    firstName: address.firstName,
    lastName: address.lastName,
    shipToPhoneNumber: address.shipToPhoneNumber
});

type TextField = Exclude<keyof AddressFormValues, 'typeId' | 'stateId'>;

interface IProps {
    show: boolean,
    address: IOpoAddress | null,
    availableTypes: IOpoAddressOption[],
    allTypes: IOpoAddressOption[],
    states: IOpoAddressOption[],
    onSave: (addressId: number | null, request: IOpoAddressRequest) => Promise<IOpoSaveResult>,
    onValidateAddress: (address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>,
    onCancel: () => void
}

interface IPendingValidation {
    response: IUsPsAddressResponse,
    submittedAddress: IAddress,
    values: AddressFormValues
}

const AddressModal = ({ show, address, availableTypes, allTypes, states, onSave, onValidateAddress, onCancel }: IProps) => {
    const [serverError, setServerError] = useState('');
    const [validation, setValidation] = useState<IPendingValidation | null>(null);
    const { control, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<AddressFormValues>({
        resolver: zodResolver(addressSchema),
        defaultValues: emptyValues('')
    });

    useEffect(() => {
        if (show) {
            reset(address ? toFormValues(address) : emptyValues(availableTypes.length == 1 ? String(availableTypes[0].id) : ''));
            setServerError('');
            setValidation(null);
        }
    }, [show, address]);

    const isStrongBox = Number(watch('typeId')) == OpoAddressType.StrongBox;
    const typeOptions = address ? allTypes : availableTypes;

    const onSubmit = async (values: AddressFormValues) => {
        setServerError('');

        const state = states.find(x => String(x.id) == values.stateId)?.abbreviation ?? '';
        let response: IAddressValidationResponse;

        try {
            response = await onValidateAddress(values.addressLine1, values.addressLine2, values.city, state, values.postalCode);
        } catch (err) {
            setServerError('Unable to validate the address');
            return;
        }

        if (response.data.success) {
            await saveAddress(values);
            return;
        }

        setValidation({
            response: response.data.results,
            submittedAddress: {
                street: values.addressLine1,
                street2: values.addressLine2,
                city: values.city,
                state,
                postalCode: values.postalCode
            },
            values
        });
    };

    const handleUseAddress = (recommended: boolean) => {
        const { response, values } = validation;

        setValidation(null);

        if (!recommended) {
            saveAddress(values);
            return;
        }

        const recommendedValues: AddressFormValues = {
            ...values,
            addressLine1: response.address1,
            addressLine2: response.address2,
            city: response.city,
            stateId: String(states.find(x => x.abbreviation == response.state)?.id ?? values.stateId),
            postalCode: response.zip4 ? `${response.zip5}-${response.zip4}` : response.zip5
        };

        reset(recommendedValues);
        saveAddress(recommendedValues);
    };

    const saveAddress = async (values: AddressFormValues) => {
        const result = await onSave(address ? address.id : null, {
            typeId: Number(values.typeId),
            label: values.label,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            addressLine3: isStrongBox ? '' : values.addressLine3,
            city: values.city,
            stateId: Number(values.stateId),
            postalCode: values.postalCode,
            note: values.note,
            firstName: isStrongBox ? values.firstName : '',
            lastName: isStrongBox ? values.lastName : '',
            shipToPhoneNumber: isStrongBox ? values.shipToPhoneNumber : ''
        });

        if (!result.success) {
            setServerError(result.message);
        }
    };

    const renderGroup = (label: string, error: string | undefined, input: JSX.Element, className?: string, required = false) => (
        <FormGroup className={[className, error ? 'has-error' : null].filter(x => x).join(' ')}>
            <ControlLabel>{required && <span className='required' aria-hidden='true'> *</span>} {label}</ControlLabel>
            {input}
            {error && (
                <HelpBlock>{error}</HelpBlock>
            )}
        </FormGroup>
    );

    const renderText = (name: TextField, label: string, maxLength: number, className?: string, required = false) => (
        <Controller control={control} name={name} render={({ field }) =>
            renderGroup(label, errors[name]?.message, <FormControl type='text' maxLength={maxLength} required={required} aria-required={required} value={field.value} onChange={field.onChange} onBlur={field.onBlur} />, className, required)
        } />
    );

    return (
        <>
            <Modal show={show} onHide={onCancel} backdrop='static'>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Modal.Header closeButton>
                        <Modal.Title>{address ? `Edit ${address.typeName} Address` : 'Add Address'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{ marginBottom: validation != null ? 15 : 0 }}>
                            <AddressValidation
                                show={validation != null}
                                response={validation?.response}
                                submittedAddresss={validation?.submittedAddress}
                                cancelLabel='Cancel & Return to Address'
                                onlyContent={true}
                                onUseAddress={handleUseAddress}
                                onCancel={() => setValidation(null)}
                            />
                        </div>

                        <OpoModalForm>
                            {serverError && (
                                <Alert bsStyle='danger'>{serverError}</Alert>
                            )}

                            <fieldset disabled={validation != null}>
                                <div className={validation == null ? 'form-grid' : 'form-grid form-grid-mask'}>
                                    <Controller control={control} name='typeId' render={({ field: { value, onChange } }) =>
                                        renderGroup('Address Type', errors.typeId?.message, (
                                            <FormControl componentClass='select' value={value} onChange={onChange} disabled={address != null} required aria-required='true'>
                                                <option value=''>Select...</option>
                                                {typeOptions.map(type =>
                                                    <option key={type.id} value={type.id}>{type.description}</option>
                                                )}
                                            </FormControl>
                                        ), undefined, true)
                                    } />

                                    {renderText('label', isStrongBox ? 'Company Name' : 'Name', 32, 'full-width', isStrongBox)}

                                    {isStrongBox && renderText('firstName', 'First Name', 50, undefined, true)}
                                    {isStrongBox && renderText('lastName', 'Last Name', 50, undefined, true)}
                                    {isStrongBox && renderText('shipToPhoneNumber', 'Ship-To Phone Number', 50, 'full-width', true)}
                                    {!isStrongBox && renderText('addressLine3', 'Attention', 64, 'full-width')}

                                    {renderText('addressLine1', 'Street Address', 64, 'full-width', true)}
                                    {renderText('addressLine2', 'Suite / PO Box', 64, 'full-width')}
                                    {renderText('city', 'City', 32, undefined, true)}

                                    <Controller control={control} name='stateId' render={({ field: { value, onChange } }) =>
                                        renderGroup('State', errors.stateId?.message, (
                                            <FormControl componentClass='select' value={value} onChange={onChange} required aria-required='true'>
                                                <option value=''>Select...</option>
                                                {states.map(state =>
                                                    <option key={state.id} value={state.id}>{state.description}</option>
                                                )}
                                            </FormControl>
                                        ), undefined, true)
                                    } />

                                    {renderText('postalCode', 'Postal Code', 10, undefined, true)}

                                    <Controller control={control} name='note' render={({ field }) =>
                                        renderGroup('Note', errors.note?.message, (
                                            <FormControl componentClass='textarea' rows={3} maxLength={200} value={field.value} onChange={field.onChange} onBlur={field.onBlur} style={{ height: 'auto', padding: '6px 10px' }} />
                                        ), 'full-width')
                                    } />
                                </div>
                            </fieldset>

                            <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B7A8F' }}>
                                <span className='required'>*</span> Required
                            </p>
                        </OpoModalForm>
                    </Modal.Body>
                    <Modal.Footer>
                        <OpoModalButtons>
                            <button type='submit' className='btn btn-primary' disabled={isSubmitting || validation != null}>{address ? 'Save Changes' : 'Add Address'}</button>
                            <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                        </OpoModalButtons>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* <AddressValidation
                show={validation != null}
                response={validation?.response}
                submittedAddresss={validation?.submittedAddress}
                cancelLabel='Cancel & Return to Address'
                onUseAddress={handleUseAddress}
                onCancel={() => setValidation(null)}
            /> */}
        </>
    );
}

export default AddressModal;
